import React from 'react';
import {
  Body,
  Tooltip,
  designSystemToken,
} from '@lightricks/react-design-system';
import numberAbbreviation from '@/utils/numberAbbreviation';
import translate from '@/utils/translate';
import type {CampaignProps} from '@/types/campaign';
import styles from './CampaignCostSummary.module.scss';

interface CampaignCostSummaryProps {
  campaign: Partial<CampaignProps>;
  allowEditCreatorBid?: boolean;
  testID?: string;
}

const COST_FORMAT = '$0,0.00';

// Accepted-cost readout for the campaign V2 tabs row. The render gate below
// matches the creators tab's pricing visibility, so the total stays hidden
// wherever V2 hides creator pricing.
function CampaignCostSummary({
  campaign,
  allowEditCreatorBid,
  testID = 'campaign-cost-summary',
}: CampaignCostSummaryProps) {
  if (
    !campaign.loaded ||
    !campaign.showCampaignDetails ||
    !campaign.isOpenMarket ||
    !allowEditCreatorBid ||
    !campaign.acceptedTotalPrice
  ) {
    return null;
  }

  const tooltipText = translate(
    'The total cost of accepted creators for this campaign. This amount has already been deducted from the brand budget.'
  );

  return (
    <div className={styles.container} data-testid={testID}>
      <Tooltip title={tooltipText} type="dark" placement="bottom" arrow>
        {/* aria-label folds the value and tooltip into the accessible name so
          the hover-only context reaches assistive tech. */}
        <div
          className={styles.section}
          data-testid={`${testID}--accepted-cost`}
          aria-label={`${numberAbbreviation(
            campaign.acceptedTotalPrice,
            COST_FORMAT
          )} ${translate('Total cost, USD')}. ${tooltipText}`}
        >
          <Body size="md" sx={{fontWeight: 600}}>
            {numberAbbreviation(campaign.acceptedTotalPrice, COST_FORMAT)}
          </Body>
          <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
            {translate('Total cost, USD')}
          </Body>
        </div>
      </Tooltip>
    </div>
  );
}

export default CampaignCostSummary;
